import { posthog as posthogSingleton } from "posthog-js"
import { hasConsented } from "./cookie-consent"

const POSTHOG_PROJECT_KEY = "phc_c7bzoVbLd8SIfUUjP485jICOeIJaHZZT35IusWlOEcm"

type Properties = Record<string, any>
type PendingIdentity = {
    distinctId: string
    properties?: Properties
    propertiesToSetOnce?: Properties
}

let initialized = false
let consentActive = false
let pendingIdentity: PendingIdentity | undefined
let lastIdentity: PendingIdentity | undefined
let identifiedUserId: string | undefined
let resetBeforePendingIdentity = false

function analyticsConsentGranted(): boolean {
    return hasConsented("analytics")
}

function resetSdkPreservingConsent() {
    posthogSingleton.reset()
    if (analyticsConsentGranted()) {
        posthogSingleton.opt_in_capturing()
        consentActive = true
    } else {
        posthogSingleton.opt_out_capturing()
        consentActive = false
    }
}

function identifyNow({ distinctId, properties, propertiesToSetOnce }: PendingIdentity) {
    const persistedUserId = posthogSingleton.get_property?.("$user_id")
    const previousUserId =
        identifiedUserId ?? (typeof persistedUserId === "string" ? persistedUserId : undefined)

    // Never merge two authenticated users on a shared browser. Preserve the
    // anonymous ID only when there is no previously identified user.
    if (previousUserId && previousUserId !== distinctId) {
        resetSdkPreservingConsent()
    }

    if (posthogSingleton.get_distinct_id() !== distinctId) {
        posthogSingleton.identify(distinctId, properties, propertiesToSetOnce)
    } else if (properties || propertiesToSetOnce) {
        posthogSingleton.setPersonProperties(properties, propertiesToSetOnce)
    }

    identifiedUserId = distinctId
}

function flushPendingIdentity() {
    if (!initialized || !consentActive || !pendingIdentity) return

    const identity = pendingIdentity
    pendingIdentity = undefined
    if (resetBeforePendingIdentity) {
        resetSdkPreservingConsent()
        identifiedUserId = undefined
        resetBeforePendingIdentity = false
    }
    identifyNow(identity)
    lastIdentity = identity
}

export function initializePosthog(): boolean {
    if (!analyticsConsentGranted()) return false

    if (!initialized) {
        posthogSingleton.init(POSTHOG_PROJECT_KEY, {
            api_host: "https://eu.i.posthog.com",
            defaults: "2025-05-24",
            person_profiles: "identified_only",
            cross_subdomain_cookie: true,
            opt_out_persistence_by_default: true,
            before_send: (event) => {
                if (!event || event.event !== "$exception") return event

                const properties = event.properties || {}
                const exceptionTypes = Array.isArray(properties.$exception_types)
                    ? properties.$exception_types
                    : []
                const exceptionValues = Array.isArray(properties.$exception_values)
                    ? properties.$exception_values
                    : []
                const exceptionList = Array.isArray(properties.$exception_list)
                    ? properties.$exception_list
                    : []
                const isCanceledAxiosRequest =
                    exceptionTypes.includes("CanceledError") && exceptionValues.includes("canceled")
                const hasCanceledException = exceptionList.some(
                    (exception: { type?: string; value?: string }) =>
                        exception?.type === "CanceledError" && exception?.value === "canceled",
                )

                return isCanceledAxiosRequest || hasCanceledException ? null : event
            },
            loaded: (posthog) => {
                const isStagingOrDev =
                    window.location.href.includes("staging") ||
                    window.location.href.includes("localhost")
                posthog.register({
                    staging: isStagingOrDev,
                })
            },
        })
        initialized = true
    }

    if (!consentActive) {
        posthogSingleton.opt_in_capturing()
        consentActive = true
    }
    flushPendingIdentity()
    return true
}

export function syncPosthogConsent() {
    if (analyticsConsentGranted()) {
        initializePosthog()
        return
    }

    if (initialized && consentActive) {
        posthogSingleton.opt_out_capturing()
        consentActive = false
        if (lastIdentity) {
            pendingIdentity = lastIdentity
            resetBeforePendingIdentity = true
        }
    }
}

export const posthog = {
    capture(name: string, properties?: Properties) {
        if (!initializePosthog()) return

        posthogSingleton.capture(name, properties)
    },

    identify(distinctId: string, properties?: Properties, propertiesToSetOnce?: Properties) {
        pendingIdentity = { distinctId, properties, propertiesToSetOnce }
        if (!initializePosthog()) return

        flushPendingIdentity()
    },

    setPersonProperties(properties: Properties, propertiesToSetOnce?: Properties) {
        if (!initializePosthog()) return

        posthogSingleton.setPersonProperties(properties, propertiesToSetOnce)
    },

    reset() {
        pendingIdentity = undefined
        lastIdentity = undefined
        identifiedUserId = undefined
        resetBeforePendingIdentity = false
        if (initialized) {
            resetSdkPreservingConsent()
        }
    },

    getDistinctId(): string | undefined {
        if (!initialized || !analyticsConsentGranted()) return undefined
        return posthogSingleton.get_distinct_id()
    },
}

export function resetPosthogStateForTests() {
    initialized = false
    consentActive = false
    pendingIdentity = undefined
    lastIdentity = undefined
    identifiedUserId = undefined
    resetBeforePendingIdentity = false
}
